import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import {
  CONSENT_EVENT,
  getConsent,
  getAdSignals,
  setConsent,
  isAnalyticsConfigured,
  isTrackedPath,
  trackPageView,
} from '../../services/analytics';
import { userApi } from '../../services/api';

/** Logged-in users: tell the server so its Meta events start or stop with the banner choice. */
function syncAdConsent() {
  const signals = getAdSignals();
  let loggedIn = false;
  try { loggedIn = Boolean(localStorage.getItem('token')); } catch (_) { /* storage blocked */ }
  if (!signals || !loggedIn) return;
  userApi.updateAdConsent(signals).catch(() => { /* best-effort; retried on next change */ });
}

/**
 * Cookie banner + route-change page views. Shown only on tracked (marketing)
 * pages, so guests opening an invitation or gallery never see it.
 */
export default function CookieConsent() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [consent, setConsentState] = useState(getConsent);

  useEffect(() => {
    const onChange = (e) => {
      setConsentState(e.detail ?? null);
      syncAdConsent();
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (consent === 'granted') trackPageView(pathname);
  }, [pathname, consent]);

  if (!isAnalyticsConfigured() || consent !== null || !isTrackedPath(pathname)) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('consent.title')}
      className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-2xl rounded-2xl bg-white dark:bg-[#1e1e2a] shadow-xl ring-1 ring-black/10 dark:ring-white/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="text-sm text-[#4A4550] dark:text-stone-300 leading-relaxed flex-1">
          {t('consent.text')}{' '}
          <a href="/privacy" className="underline underline-offset-2 hover:text-[#9A4357]">{t('consent.privacy')}</a>
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setConsent('denied')}
            className="px-4 py-2 rounded-full text-sm font-medium text-[#4A4550] dark:text-stone-300 ring-1 ring-black/15 dark:ring-white/20 hover:bg-black/5 dark:hover:bg-white/5 transition"
          >
            {t('consent.decline')}
          </button>
          <button
            type="button"
            onClick={() => setConsent('granted')}
            className="px-4 py-2 rounded-full text-sm font-medium text-white bg-[#9A4357] hover:bg-[#84384A] transition"
          >
            {t('consent.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
